import { useTranslation } from 'react-i18next';
import { formatGetal, isObject, isTelling } from '../format';
import { useDoelOpslaan, useWatAlsDoel } from '../hooks/useInstellingen';
import { weeknummer } from '../week';
import type { WatAlsDoel } from '../work-quality.api';
import { Instellingformulier, WERKSOORT_VOLGORDE, naamVan, opVoornaam } from './Instellingformulier';
import type { Effect } from './Instellingformulier';

export interface GoalFormProps {
  /** Het doel dat nu geldt, zoals de dienst het teruggaf. */
  huidigDoel: number;
  /** De laatst afgesloten week, of leeg zolang er nog geen enkele is. */
  week: string;
}

/** Het bereik van het doel ligt vast: hele procenten van 1 tot en met 100 (CAP-4, M2). */
const DOEL_MIN = 1;
const DOEL_MAX = 100;

/**
 * Het Doelformulier (story 12, S7): één doel voor Vastleggen en Controles samen, en eronder per
 * werksoort wie er met dat doel in de laatst afgesloten week in groep Aandacht valt.
 *
 * **Geen eigen rekenwerk over mensen.** Elke telling komt uit de wat-als; het scherm telt alleen de
 * lengte van de lijst en sorteert de namen. Klopt een telling niet met de andere, dan staat er voor
 * die werksoort dat het antwoord het niet goed noemt, in plaats van een getal over mensen.
 *
 * Het doelantwoord noemt het doel niet terug; de zin noemt de waarde waarover gevraagd is, die het
 * formulier samen met het antwoord onthoudt.
 */
export function GoalForm({ huidigDoel, week }: GoalFormProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const opslag = useDoelOpslaan();
  const getal = (waarde: number) => formatGetal(waarde, locale);

  function effect(antwoord: WatAlsDoel, doel: number): Effect {
    const nietGoed = t('qa.instellingen.effectDoelNietGoed');
    const per = isObject(antwoord.perWerksoort) ? antwoord.perWerksoort : {};
    const w = weeknummer(week);

    const blokken = WERKSOORT_VOLGORDE.flatMap(([sleutel, labelSleutel]) => {
      const blok: unknown = per[sleutel];
      // Een blok of namenlijst die geen object/lijst is, telt als niet genoemd.
      if (!isObject(blok) || !Array.isArray(blok.aandacht)) return [];
      const { aantalMetCijfer: totaal, aantalOnderDoel: onder } = blok;
      const aandacht: unknown[] = blok.aandacht;
      const werksoort = t(labelSleutel);
      const gedeeld = { doel: getal(doel), week: w, werksoort };

      let zin: string;
      let gezegd: string;
      let namen: string[] = [];
      // Een lijst met iets anders dan personen telt niet goed: "2 van hen" zou dan één naam tonen.
      if (
        !isTelling(totaal) ||
        !isTelling(onder) ||
        !aandacht.every(isObject) ||
        aandacht.length > onder ||
        onder > totaal
      ) {
        zin = gezegd = nietGoed;
      } else if (totaal === 0) {
        zin = gezegd = t('qa.instellingen.effectDoelGeenCijfer', gedeeld);
      } else if (onder === 0) {
        zin = gezegd = t('qa.instellingen.effectDoelNiemandOnder', { ...gedeeld, totaal: getal(totaal) });
      } else {
        const begin = t('qa.instellingen.effectDoelOnder', {
          ...gedeeld,
          count: onder,
          onder: getal(onder),
          totaal: getal(totaal),
        });
        const n = aandacht.length;
        zin = `${begin} ${n === 0 ? t('qa.instellingen.effectDoelGeenAandacht') : t('qa.instellingen.effectDoelAandacht', { count: n, aantal: getal(n) })}`;
        gezegd = `${begin} ${n === 0 ? t('qa.instellingen.effectDoelGeenAandacht') : t('qa.instellingen.effectDoelAandachtGezegd', { count: n, aantal: getal(n) })}`;
        namen = opVoornaam(aandacht as Array<Record<string, unknown>>, locale).map((persoon) => naamVan(persoon, t));
      }
      return [{ sleutel, werksoort, zin, gezegd, namen }];
    });

    if (blokken.length === 0) return { inhoud: <p>{nietGoed}</p>, samenvatting: nietGoed };

    return {
      samenvatting: blokken.map((blok) => blok.gezegd).join(' '),
      inhoud: blokken.map((blok) => (
        <div className="flex flex-col gap-1" key={blok.sleutel}>
          <h3 className="text-[13px] font-semibold text-[var(--foreground-heading)]">{blok.werksoort}</h3>
          <p>{blok.zin}</p>
          {blok.namen.length > 0 && (
            <ul className="flex list-disc flex-col gap-0.5 pl-5">
              {blok.namen.map((naam, plek) => (
                <li className="break-words" key={`${naam}-${plek}`}>
                  {naam}
                </li>
              ))}
            </ul>
          )}
        </div>
      )),
    };
  }

  return (
    <Instellingformulier<WatAlsDoel>
      bereikMelding={t('qa.instellingen.doelBereikMelding')}
      bevestiging={(doel) => t('qa.instellingen.doelOpgeslagen', { doel: getal(doel) })}
      effect={effect}
      eenheid={t('qa.instellingen.doelEenheid')}
      huidigeWaarde={huidigDoel}
      kop={t('qa.instellingen.doelKop')}
      label={t('qa.instellingen.doelLabel')}
      max={DOEL_MAX}
      min={DOEL_MIN}
      misluktTekst={t('qa.instellingen.doelOpslaanMislukt')}
      opslag={{ ...opslag, opgeslagen: opslag.opgeslagenDoel }}
      kind="doel"
      uitleg={t('qa.instellingen.doelUitleg')}
      useGevolg={useWatAlsDoel}
      week={week}
    />
  );
}
